<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="子データ構成モデルを追加" v-if="isEditable">
        <v-btn icon :disabled="!selectedDataTranslationMap" @click="openCreateDataTranslationMapDialog(selectedDataTranslationMap)">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </el-tooltip>

      <div class="dpd-table-header" :style="{left: leftTableWidth}">
        <span style="color: rgba(255,255,255,0.9); margin-right: 10px;">データ項目定義</span>
      </div>
    </template>

    <multipane @paneResize="handleMultipaneMove" @paneResizeStop="handleMultipaneMove">
      <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
          データ変換マップテーブル
       - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
      <el-table
          :data="dataTranslationMapView"
          :row-class-name="dataTranslationMapTableRowClassName"
          :style="{width: leftTableWidth, minWidth: '300px'}"
          :height="tabItemBounds.height"
          row-key="id"
          class="data-translation-map-table"
          ref="dataTranslationMapTable"
          @current-change="selectedDataTranslationMap = $event"
          default-expand-all>
        <el-table-column
            label="データ連結モデル名"
            column-key="data-relation-model-name"
            class-name="data-relation-model-name">
          <template #default="{row}">
            <span v-if="row.dataRelationModel && row.dataRelationModel.name">{{ row.dataRelationModel.name }}</span>
            <span v-else>{{ row.targetDataComponentModel.name }}</span>
          </template>
        </el-table-column>
        <el-table-column
            v-if="isEditable"
            width="60"
            align="right">
          <template #default="{row}">
            <icon-btns>
              <icon-btn
                  v-if="row.parentId"
                  type="delete"
                  @click="openDeleteDataTranslationMapDialog(row)">
              </icon-btn>
            </icon-btns>
          </template>
        </el-table-column>
      </el-table>
      <multipane-resizer></multipane-resizer>

      <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
          共通辞書データ項目定義テーブル
       - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
      <el-table
          :data="commonDataPropertyDefinitionView"
          :cell-class-name="commonDataPropertyDefinitionViewCellClassName"
          :row-class-name="commonDataPropertyDefinitionViewRowClassName"
          :height="tabItemBounds.height"
          class="common-data-property-definition-table"
          ref="commonDataPropertyDefinitionTable">
        <el-table-column
            width="200"
            label="データ連結モデル名"
            column-key="dataTranslationMap.targetDataComponentModel.name">
          <template #default="{row}">
            <span :set="dtm = row.dataTranslationMap">
              <span v-if="dtm.dataRelationModel && dtm.dataRelationModel.name">{{ dtm.dataRelationModel.name }}</span>
              <span v-else>{{ dtm.targetDataComponentModel.name }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column
            width="50"
            align="center"
            label="No"
            prop="index"
            column-key="index">
          <template #default="{row}">
            <el-tooltip v-if="row.isPrimaryKey" placement="bottom" content="主キー">
              <span>{{ row.index }}</span>
            </el-tooltip>
            <span v-else>{{ row.index }}</span>
          </template>
        </el-table-column>
        <el-table-column
            align="center"
            label="主キー"
            column-key="isPrimaryKey"
            width="80">
          <template #default="{row}">
            <table-inline>
              <readonly-checkbox
                  :value="row.isPrimaryKey">
              </readonly-checkbox>
            </table-inline>
          </template>
        </el-table-column>
        <el-table-column
            min-width="25"
            label="名称"
            prop="name">
        </el-table-column>
        <el-table-column
            width="120"
            align="center"
            label="型"
            column-key="dataType">
          <template #default="{row}">
            {{ dataTypes[row.dataType] }}
          </template>
        </el-table-column>
        <el-table-column
            width="50"
            align="center"
            label="必須"
            column-key="isRequired">
          <template #default="{row}">
            <table-inline>
              <readonly-checkbox
                  :value="row.isRequired">
              </readonly-checkbox>
            </table-inline>
          </template>
        </el-table-column>
        <el-table-column
            label="説明"
            prop="target.description"
            min-width="50">
          <template #default="{row}">
            <table-inline>
              <text-field-like
                  :value="row.target && row.target.description"
                  placeholder="未設定"
                  multi-line>
              </text-field-like>
            </table-inline>
          </template>
        </el-table-column>
      </el-table>
    </multipane>

    <!-- データ連結追加ダイアログ -->
    <simple-dialog
        :value="createDataTranslationMapDialog"
        title-text="データ連結追加"
        @close="closeCreateDataTranslationMapDialog">
      <v-row>
        <v-col cols="12" :set="parentDataTranslationMap = createDataTranslationMapDialog.parentDataTranslationMap">
          <text-field-like
              label="連結元となるデータ構成モデル"
              :value="parentDataTranslationMap ? parentDataTranslationMap.targetDataComponentModel.name : ''">
          </text-field-like>
        </v-col>
        <v-col cols="12">
          <model-select
              label="連結先データ構成モデル (設定されている場合にはデータ連結モデル名)"
              v-model="createDataTranslationMapDialog.childDataRelationModel"
              :items="createDataTranslationMapDialog.dataRelationModels"
              :itemText="dataRelationModelToString"
              :loading="createDataTranslationMapDialog.loading"
              :rules="[v => !!v || '連結先データ構成モデルを選択して下さい']"
              return-object>
          </model-select>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- データ連結解除ダイアログ -->
    <simple-dialog
        :value="deleteDataTranslationMapDialog"
        title-text="データ連結解除"
        title-color="delete"
        apply-text="解除"
        header-text="以下のデータ連結を解除します。本当によろしいですか？"
        @close="closeDeleteDataTranslationMapDialog">
      <v-row>
        <v-col cols="12" :set="parentDataTranslationMap = deleteDataTranslationMapDialog.parentDataTranslationMap">
          <text-field-like
              label="連結元となるデータ構成モデル"
              :value="parentDataTranslationMap ? parentDataTranslationMap.targetDataComponentModel.name : ''">
          </text-field-like>
        </v-col>
        <v-col cols="12" :set="childDataTranslationMap = deleteDataTranslationMapDialog.childDataTranslationMap">
          <text-field-like
              label="連結先データ構成モデル (設定されている場合にはデータ連結モデル名)"
              :value="dataTranslationMapToString(childDataTranslationMap)">
          </text-field-like>
        </v-col>
      </v-row>
    </simple-dialog>

  </tab-item-template>
</template>

<script>
import {Multipane, MultipaneResizer} from "vue-multipane";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import PathInfoMixin from "@/router/PathInfoMixin";
import {mapActions, mapGetters, mapMutations} from "vuex";
import TableInline from "@/components/atoms/TableInline.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import {DATA_PROPERTY_DEFINITION_DATA_TYPES} from "@/constants";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import gql from "graphql-tag";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import scrollIntoView from "scroll-into-view-if-needed";
import IoablePageMixin from "@/router/IoablePageMixin";
import DataTranslationMapAddGql from "@/gql/DataComponentModel/DataTranslationMapAdd.gql";

export default {
  name: "ExtendedDataPropertyDefinition",
  components: {
    IconBtn,
    IconBtns,
    TextFieldLike,
    ModelSelect, SimpleDialog, ReadonlyCheckbox, TableInline, TabItemTemplate, Multipane, MultipaneResizer},
  mixins: [
    PathInfoMixin,
    IoablePageMixin,
  ],
  props: {
    dictionary: {
      type: Object,
      required: true
    },
    dataComponentModel: {
      type: Object,
      required: true
    },
    selectedDictionary: {
      type: Object,
      required: true
    },
    selectedDataComponentModel: {
      type: Object,
      required: true
    }
  },
  inject: [
    "tabItemBounds"
  ],
  data() {
    return {
      leftTableWidth: '300px',
      dataTranslationMapView: [],
      selectedDataTranslationMap: null,
      createDataTranslationMapDialog: false,
      deleteDataTranslationMapDialog: false,
      dataTypes: DATA_PROPERTY_DEFINITION_DATA_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      entities: 'hds/entities',
    }),
    commonDataPropertyDefinitionView() {
      const dpds = [];
      function flatten(dtmNode) {
        dtmNode.targetDataComponentModel.dataPropertyDefinitions
            .map(dpd => ({...dpd, dataTranslationMap: dtmNode}))
            .forEach(dpd => dpds.push(dpd));

        if(dtmNode.children) {
          dtmNode.children.forEach(dtmChildren => flatten(dtmChildren));
        }
      }
      this.dataTranslationMapView.forEach(dtmNode => flatten(dtmNode));
      return dpds;
    },
    dataRelationModelIdView() {
      const drmIds = [];
      function flatten(dtmNode) {
        if(dtmNode.dataRelationModelId) {
          drmIds.push(dtmNode.dataRelationModelId);
        }
        if(dtmNode.children) {
          dtmNode.children.forEach(dtmChildren => flatten(dtmChildren));
        }
      }
      this.dataTranslationMapView.forEach(dtmNode => flatten(dtmNode));
      return drmIds;
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad',
    }),
    async load() {
      this.updateDataTranslationMapView();
    },
    updateDataTranslationMapView() {
      const treeMap = new Map();
      for(const dtm of this.entities.dataTranslationMap.find()) {
        let children = treeMap.get(dtm.parentId);
        if(!children) {
          treeMap.set(dtm.parentId, children = []);
        }
        children.push(dtm);
      }

      function buildTree(node) {
        const children = treeMap.get(node.id) || [];
        return {
          ...node,
          children: children.map(child => buildTree(child))
        };
      }

      const root = treeMap.get(null)?.find(dtm => dtm.targetDataComponentModelId === this.selectedDataComponentModel.id);
      this.dataTranslationMapView = root ? [buildTree(root)] : [];
    },
    findDataTranslationMapViewRow(dataTranslationMapId) {
      function find(dtmNode) {
        if(dtmNode.id === dataTranslationMapId) {
          return dtmNode;
        }
        for(const child of dtmNode.children) {
          const found = find(child);
          if(found) {
            return found;
          }
        }
        return null;
      }

      if(this.dataTranslationMapView.length) {
        return find(this.dataTranslationMapView[0]);
      } else {
        return null;
      }
    },
    findDataTranslationMapViewDescendants(dataTranslationMap) {
      if(typeof dataTranslationMap === 'string' || dataTranslationMap instanceof String) {
        dataTranslationMap = this.findDataTranslationMapViewRow(dataTranslationMap);
      }
      if(!dataTranslationMap) {
        return [];
      }

      const result = [];
      function flatten(dtmNode) {
        result.push(dtmNode);
        for(const child of dtmNode.children) {
          flatten(child);
        }
      }
      flatten(dataTranslationMap);
      return result;
    },

    dataRelationModelToString(drm) {
      if(!drm) {
        return '';
      } else if(drm.name) {
        return `${drm.targetDataComponentModel.name} (${drm.name})`;
      } else {
        return drm.targetDataComponentModel.name;
      }
    },
    dataTranslationMapToString(dtm) {
      if(!dtm) {
        return '';
      }
      const drm = dtm.dataRelationModel;
      if(drm?.name) {
        return `${dtm.targetDataComponentModel.name} (${drm.name})`;
      } else {
        return dtm.targetDataComponentModel.name;
      }
    },

    handleMultipaneMove(pane, resizer, size) {
      this.leftTableWidth = `${pane.clientWidth}px`;
    },
    dataTranslationMapTableRowClassName({row}) {
      if(this.selectedDataTranslationMap?.id === row.id) {
        return 'highlight-row';
      }
      return '';
    },
    commonDataPropertyDefinitionViewCellClassName({row, column}) {
      if (column.property === 'index' && row.isPrimaryKey) {
        return 'primary-key';
      }
      return '';
    },
    commonDataPropertyDefinitionViewRowClassName({row}) {
      if(this.selectedDataTranslationMap?.id === row.dataTranslationMap.id) {
        return 'highlight-row';
      }
      return '';
    },

    async openCreateDataTranslationMapDialog(parent) {
      this.createDataTranslationMapDialog = {
        parentDataTranslationMap: parent,
        childDataRelationModel: null,
        loading: true,
        dataRelationModels: []
      };

      const {dataComponentModel} = await this.gqlQuery({
        query: gql`
            query FindDataReleationModels($targetDataComponentModelId: ID!) {
                dataComponentModel(dataComponentModelId: $targetDataComponentModelId) {
                    dataRelationModels {
                        id
                        name
                        targetDataComponentModel {
                            id
                            name
                        }
                    }
                }
            }
        `,
        variables: {
          targetDataComponentModelId: parent.targetDataComponentModel.id
        }
      });

      this.createDataTranslationMapDialog.loading = false;
      this.createDataTranslationMapDialog.dataRelationModels =
          dataComponentModel.dataRelationModels.filter(drm => !this.dataRelationModelIdView.includes(drm.id));
    },
    async closeCreateDataTranslationMapDialog(apply) {
      if(apply) {
        try {
          this.showSnackbar({
            message: '拡張データ構成モデルを作成中...',
            timeout: false,
            loading: true
          });

          const {dataTranslationMaps} = await this.gqlMutateAndLoad({
            mutation: DataTranslationMapAddGql,
            variables: {
              addDataTranslationMaps: [{
                sourceDataComponentModelId: this.dataComponentModel.id,
                targetDataComponentModelId: this.createDataTranslationMapDialog.childDataRelationModel.targetDataComponentModel.id,
                parentId: this.createDataTranslationMapDialog.parentDataTranslationMap.id,
                dataRelationModelId: this.createDataTranslationMapDialog.childDataRelationModel.id,
                dataPropertyTranslationMaps: [],
              }]
            },
            mapping: {
              dataTranslationMap: 'dataTranslationMaps?',
              dataComponentModel: 'dataTranslationMaps.targetDataComponentModel*?',
              dataPropertyDefinition: 'dataTranslationMaps.targetDataComponentModel.dataPropertyDefinitions*?'
            }
          });

          this.updateDataTranslationMapView();
          this.selectedDataTranslationMap = this.findDataTranslationMapViewRow(dataTranslationMaps[0].id);
          this.showSnackbar({
            message: '拡張データ構成モデルを作成しました',
          });
        } catch (e) {
          console.error(e);
          this.showSnackbar({
            color: 'failed',
            message: '拡張データ構成モデルの作成に失敗しました',
          });
        }
      }
      this.createDataTranslationMapDialog = false;
    },

    openDeleteDataTranslationMapDialog(child) {
      this.deleteDataTranslationMapDialog = {
        parentDataTranslationMap: this.entities.dataTranslationMap.findOne({id: child.parentId}),
        childDataTranslationMap: child
      };
    },
    async closeDeleteDataTranslationMapDialog(apply) {
      const {parentDataTranslationMap, childDataTranslationMap} = this.deleteDataTranslationMapDialog;
      if(apply) {
        try {
          this.showSnackbar({
            message: '拡張データ構成モデルの削除中...',
            timeout: false,
            loading: true
          });

          const dataTranslationMap = this.entities.dataTranslationMap;
          const dataPropertyTranslationMap = this.entities.dataPropertyTranslationMap;

          // 削除するデータ変換マップの子孫ノードをすべて収集
          const removeDtms = this.findDataTranslationMapViewDescendants(childDataTranslationMap);

          // 削除するデータプロパティー変換マップをすべて収集
          const removeDptms = removeDtms.flatMap(dtm =>
              dataPropertyTranslationMap.find({dataTranslationMapId: dtm.id}));


          // 変更を記録せずに dataTranslationMap の該当データを削除する
          const orgDtmDca = dataTranslationMap.disableChangesApi;
          const orgDptmDca = dataPropertyTranslationMap.disableChangesApi;
          try {
            dataTranslationMap.disableChangesApi = true;
            for(const removeDtm of removeDtms) {
              dataTranslationMap.findAndRemove({id: removeDtm.id});
            }

            dataPropertyTranslationMap.disableChangesApi = true;
            for(const removeDptm of removeDptms) {
              dataPropertyTranslationMap.findAndRemove({id: removeDptm.id});
            }
          } finally {
            dataPropertyTranslationMap.disableChangesApi = orgDptmDca
            dataTranslationMap.disableChangesApi = orgDtmDca;
          }

          // サーバーサイドでもミューテート
          await this.gqlMutate({
            mutation: gql`
              mutation DataTranslationMapRemove(
                $removeDataTranslationMapIds: [ID!]!,
              ) {
                removeDataTranslationMaps(dataTranslationMapIds: $removeDataTranslationMapIds)
              }`,
            variables: {
              removeDataTranslationMapIds: removeDtms.map(dtm => dtm.id),
            },
          });

          this.updateDataTranslationMapView(); // テーブルを更新
          this.selectedDataTranslationMap = parentDataTranslationMap; // 親データ変換マップを選択させる
          this.showSnackbar({
            message: '拡張データ構成モデルを削除しました',
          });
        } catch (e) {
          console.error(e);
          this.showSnackbar({
            color: 'failed',
            message: '拡張データ構成モデルの削除に失敗しました',
          });
        }
      }
      this.deleteDataTranslationMapDialog = false;
    },
  },
  watch: {
    selectedDataTranslationMap(newValue, oldValue) {
      if(newValue?.id === oldValue?.id) {
        return;
      }
      this.$refs.dataTranslationMapTable.setCurrentRow(newValue);
      this.$nextTick(() => {
        const tableEl = this.$refs.commonDataPropertyDefinitionTable.$el;
        scrollIntoView(tableEl.querySelector('.highlight-row'), {
          behavior: 'smooth',
          boundary: tableEl.querySelector('.el-table__body-wrapper')
        });
      });
    },
    dataTranslationMapView: {
      immediate: true,
      handler(newValue) {
        if(newValue?.length && this.selectedDataTranslationMap === null) {
          this.selectedDataTranslationMap = newValue[0];
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "src/theme";

::v-deep {
  .primary-key {
    @extend .primary-key-base;
  }

  .el-table.data-translation-map-table {
    td.data-relation-model-name {
      cursor: default;
    }

    tr.highlight-row {
      @include tr-background-color($tableRowHighlightColor, true);
    }
  }

  .el-table.common-data-property-definition-table {
    tr.highlight-row {
      @include tr-background-color($tableRowHighlightColor, true);
    }
  }
}

.dpd-table-header {
  position: absolute;
  padding-left: 10px;
  white-space: pre;
  overflow: hidden;
}
</style>
